import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// Rendered from the apps on Sylvester's own Mac, as its Dock shows them.
import finder from "../assets/icons/dock/finder.png";
import notes from "../assets/icons/dock/notes.png";
import zed from "../assets/icons/dock/zed.png";
import chrome from "../assets/icons/dock/chrome.png";
import ghostty from "../assets/icons/dock/ghostty.png";
import clockFace from "../assets/icons/dock/clock-face.png";
import clockHour from "../assets/icons/dock/clock-hour.png";
import clockMinute from "../assets/icons/dock/clock-minute.png";
import orbstack from "../assets/icons/dock/orbstack.png";
import spotify from "../assets/icons/dock/spotify.png";
import binFull from "../assets/icons/dock/trash-full.png";
import binEmpty from "../assets/icons/dock/trash-empty.png";
import { showNotes } from "../redux/windowSlice";
import { setCurrentApp, setCurrentSettings } from "../redux/appSlice";
import usePlayer from "../hooks/usePlayer";
import useClock from "../hooks/useClock";
import { notify } from "./Notification";

// Magnification, as macOS does it: icons near the cursor grow along a smooth curve.
const BASE = 64;
const MAX = 96;
const RANGE = 180;

// Clock's Dock icon shows the time, like the real one: the hands turn over a hands-free face.
const ClockIcon = () => {
  const now = useClock(1000);
  const m = now.getMinutes() + now.getSeconds() / 60;
  const h = (now.getHours() % 12) + m / 60;
  return (
    <span className="dock-clock">
      <img src={clockFace} alt="" draggable={false} />
      <img
        src={clockHour}
        alt=""
        draggable={false}
        className="dock-clock-hand"
        style={{ transform: `rotate(${h * 30}deg)` }}
      />
      <img
        src={clockMinute}
        alt=""
        draggable={false}
        className="dock-clock-hand"
        style={{ transform: `rotate(${m * 6}deg)` }}
      />
    </span>
  );
};

const DockItem = ({ name, icon, onClick, running, launching }) => (
  <div className={`dock-item ${launching ? "is-launching" : ""}`}>
    <button
      type="button"
      onClick={onClick}
      // Pointing at an app's icon gets its minimized window's restore animation ready.
      onPointerEnter={() => window.dispatchEvent(new CustomEvent("genie-prepare", { detail: name }))}
      className="dock-button"
      aria-label={name}
    >
      <span className="dock-tip glass glass--light">{name}</span>
      {icon === "clock" ? <ClockIcon /> : <img src={icon} alt="" draggable={false} />}
    </button>
    {running && <span className="dock-dot" aria-hidden="true" />}
  </div>
);

const Dock = () => {
  const disptach = useDispatch();
  const { snotes, show: finderOpen, sspotify: spotifyOpen, binEmptied } = useSelector((state) => state.window);
  const { playing } = usePlayer();
  const [launching, setLaunching] = useState(null);
  const navRef = useRef(null);

  const notesTexts = [
    "Double-click the desktop Notes icon or click the dock icon to view my resume.",
    "Access my resume by double-clicking Notes on the desktop or clicking the Notes icon in the dock.",
    "View my resume: double-click Notes on the desktop or click the Notes icon in  the dock.",
    "To see my resume, double-click the desktop's Notes icon or click the Notes icon in dock.",
    "Click either the desktop Notes icon or the Notes icon in the dock to view my resume.",
    "Open my resume by double-clicking Notes on the desktop or clicking the Notes icon down here.",
  ];

  const bounce = (name) => {
    setLaunching(name);
    setTimeout(() => setLaunching(null), 900);
  };

  const toggleNotes = () => {
    disptach(showNotes(!snotes));
    if (snotes) {
      disptach(setCurrentApp("Finder"));
      disptach(setCurrentSettings(["File", "Edit", "View", "Go", "Windows", "Help"]));
    } else {
      bounce("Notes");
      disptach(setCurrentApp("Notes"));
      disptach(setCurrentSettings(["File", "Edit", "Format", "View", "Window", "Help"]));
    }
  };

  // Spotify opens its window (bouncing if it wasn't open or playing).
  const openSpotify = () => {
    if (!playing && !spotifyOpen) bounce("Spotify");
    window.dispatchEvent(new Event("open-spotify"));
  };

  // Finder is always running; clicking it opens (or brings forward) its window.
  const openFinder = () => {
    if (!finderOpen) bounce("Finder");
    window.dispatchEvent(new CustomEvent("open-finder"));
  };

  // The Bin opens in Finder.
  const openBin = () => {
    if (!finderOpen) bounce("Finder");
    window.dispatchEvent(new CustomEvent("open-finder", { detail: { folder: "bin" } }));
  };

  const notesInfo = () => {
    notify(notesTexts[Math.floor(Math.random() * notesTexts.length)]);
  };

  // Magnification: each frame, every icon eases toward a size set by its distance from the cursor.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let mouseX = null;
    let raf = null;
    const sizes = new Map();

    const tick = () => {
      let moving = false;
      nav.querySelectorAll(".dock-item").forEach((el) => {
        const r = el.getBoundingClientRect();
        const d = mouseX === null ? Infinity : Math.abs(mouseX - (r.left + r.width / 2));
        const target = d < RANGE ? BASE + ((MAX - BASE) * (Math.cos((Math.PI * d) / RANGE) + 1)) / 2 : BASE;
        const current = sizes.get(el) ?? BASE;
        let next = current + (target - current) * 0.28;
        if (Math.abs(target - next) < 0.15) next = target;
        else moving = true;
        sizes.set(el, next);
        el.style.setProperty("--size", `${next}px`);
      });
      raf = moving ? requestAnimationFrame(tick) : null;
    };
    const run = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    const onMove = (e) => {
      mouseX = e.clientX;
      run();
    };
    const onLeave = () => {
      mouseX = null;
      run();
    };
    nav.addEventListener("mousemove", onMove);
    nav.addEventListener("mouseleave", onLeave);
    return () => {
      nav.removeEventListener("mousemove", onMove);
      nav.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <nav ref={navRef} className="dock glass glass--clear no-select" aria-label="Dock">
      <DockItem name="Finder" icon={finder} onClick={openFinder} running launching={launching === "Finder"} />
      <DockItem name="Notes" icon={notes} onClick={toggleNotes} running={snotes} launching={launching === "Notes"} />
      <span className="dock-sep" />
      <DockItem name="Zed" icon={zed} onClick={() => window.dispatchEvent(new Event("open-zed"))} running />
      <DockItem
        name="Google Chrome"
        icon={chrome}
        onClick={() => window.dispatchEvent(new Event("open-chrome"))}
        running
      />
      <DockItem
        name="Ghostty"
        icon={ghostty}
        onClick={() => window.dispatchEvent(new Event("open-ghostty"))}
        running
      />
      <DockItem name="Clock" icon="clock" onClick={notesInfo} running />
      <DockItem
        name="OrbStack"
        icon={orbstack}
        onClick={() => window.dispatchEvent(new Event("open-orbstack"))}
        running
      />
      <DockItem
        name="Spotify"
        icon={spotify}
        onClick={openSpotify}
        running={playing || spotifyOpen}
        launching={launching === "Spotify"}
      />
      <span className="dock-sep" />
      <DockItem name="Bin" icon={binEmptied ? binEmpty : binFull} onClick={openBin} />
    </nav>
  );
};

export default Dock;
